import React, { useContext, useEffect } from "react";
import { BusinessText, BusinessTitle, PageHeaderWrapper, OnboardingButtonContainer, OverallContainer } from "../styles";
import { Button } from "../../../components/common/Button";
import { Controller, useForm } from "react-hook-form";
import FormField from "../../../components/FormField/FormField";
import FormSection from "../components/FormSection";
import { InputPhone } from "../../../components/common/InputPhone";
import SocialMediaPicker from "../components/SocialMediaPicker";
import { BusinessContact } from "../Types";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { addBusinessContact, getParticularStep } from "../redux/actions";
import Loader from "../../../components/common/Loader";
import { NetworkErrorAlertContext } from "../../../context/NetworkErrorAlert";
import { useWindowSize } from "@reach/window-size";


type Props = {
  handleNext: () => void;
  handlePrevious: () => void;
};

const BusinessContactDetails = ({ handleNext, handlePrevious }: Props) => {
  const dispatch = useAppDispatch();
  const { width } = useWindowSize()
  const { businessId, userObj } = useAppSelector((state) => state.users || {});
  const businessCountry = userObj?.accountUser?.businessCountry;
  const { loading } = useAppSelector((state) => state.onboarding || {});

  const { onShowAlert: onShowErrorAlert } =
    useContext(NetworkErrorAlertContext) || {};

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    reset,
  } = useForm<BusinessContact>({mode: 'onChange'});

  const handleCreateBusinessContact = async (data: BusinessContact) => {
    const payload = {
      ...data,
      businessId: businessId,
      email: data.email,
      socialMediaValues: data.socialMediaValues || [],
    };
    const action = await dispatch(addBusinessContact(payload));

    if (addBusinessContact.fulfilled.match(action)) {
      handleNext();
    } else if (addBusinessContact.rejected.match(action)) {
      onShowErrorAlert("", String(action.payload));
    }
  };

  const getSavedBusinessContactDetails = async () => {
    const stepNumber = 2
    const action = await dispatch(getParticularStep({businessId, stepNumber}))
    
      if(getParticularStep.fulfilled.match(action)) {
        reset({
          email: action?.payload?.email,
          phone: action?.payload?.phone,
          website: action?.payload?.website,
          socialMediaValues: action?.payload?.socialMediaValues,
        })
      } else if (getParticularStep.rejected.match(action)) {
        console.error('We could not retrieve previously saved data.');
      }
  }
  
  useEffect(() => {
    getSavedBusinessContactDetails()
  }, [])

  return (
    <OverallContainer>
      <Loader isLoading={loading} />
      <PageHeaderWrapper>
        <BusinessTitle>Business contact details</BusinessTitle>
        <BusinessText>
          Provide your business's key contact details
        </BusinessText>
      </PageHeaderWrapper>

      <FormSection>
        <FormField
          name="email"
          control={control}
          label="Business e-mail address"
          required={true}
          errors={errors}
          rules={{
            required: "Email is required",
            pattern: {
              value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
              message: "Invalid email address",
            },
          }}
        />
      </FormSection>

      <FormSection>
        <Controller
          name="phone"
          control={control}
          rules={{ 
            required: "Phone number is required",
            pattern: {
              value: /^\+?[1-9]\d{1,14}$/,
              message: "Invalid phone number",
            }, 
          }}
          render={({ field }) => (
            <InputPhone
              label="WhatsApp Phone number"
              placeholder="Enter phone number"
              field={field}
              hasError={!!errors.phone}
              height="48px"
              defaultCountry={businessCountry}
            />
          )}
        />
        {errors.phone && <span className="error">{errors.phone.message}</span>}
      </FormSection>

      <FormSection>
        <Controller
          name="website"
          control={control}
          rules={{
            required: true,
            pattern: {
              value: /^(?:(?:https?:\/\/)?(?:www\.)?(?:[a-zA-Z0-9-]+\.)+)[a-zA-Z]{2,}(?:\/[-a-zA-Z0-9@:%._+~#=]*)*(?:\?[-a-zA-Z0-9@:%._+~#=&]*)?$/,
              message: "Please enter a valid URL (e.g., www.example.com)",
            },
          }}
          render={({ field: { onChange, value } }) => (
            <SocialMediaPicker
              value={value || undefined}
              onChange={onChange}
              addonBefore={"https://"}
              placeholder="Business website"
            />
          )}
        />

        {errors.website && (
          <span className="error">{errors.website.message}</span>
        )}
      </FormSection>

      <FormSection>
        <h1 className="business-logo-copy">Social media username/links</h1>
        <p className="business-logo-text">
          If your business operates on social media, share the account you use to connect with customers.
        </p>

        <Controller
          name="socialMediaValues"
          control={control}
          render={({ field: { onChange, value } }) => (
            <SocialMediaPicker
              addonBefore={['Instagram', 'Linkedin', 'X (formerly Twitter)', 'Facebook', 'WeChat']}
              placeholder="Enter your username or profile link here..."
              value={value}
              onChange={onChange}
            />
          )}
        />

        {errors?.socialMediaValues && (
          <span className="error">
            {errors?.socialMediaValues?.message?.toString()}
          </span>
        )}
      </FormSection>

      <OnboardingButtonContainer>
        <Button
          label="Back"
          theme="alternate"
          height="48px"
          fontSize="16px"
          width="150px"
          onClick={handlePrevious}
        />
        <Button
          onClick={handleSubmit(handleCreateBusinessContact)}
          label="Continue"
          width={width < 1150 ? '' : '494px' }
          height="48px"
          fontSize="16px"
          disabled={!isValid || loading}
        />
      </OnboardingButtonContainer>
    </OverallContainer>
  );
};

export default BusinessContactDetails;
